import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "@radix-ui/react-checkbox";
import {
  AlertCircle,
  Clock,
  CheckCircle,
  Calendar,
  Package,
  DollarSign,
} from "lucide-react";
import { maintenanceService } from "../../services/maintenance/planMaestroService";
import { toast } from "react-hot-toast";

interface ActividadPostergadaItem {
  id: number;
  item_catalogo: {
    nombre: string;
    codigo_interno: string;
    unidad_medida: string;
  };
  cantidad: number;
  costo_estimado: number | string; // Puede venir como string desde el backend
  precio_cotizado: number | string | null; // Puede venir como string desde el backend
  proveedor?: {
    razon_social: string;
  };
  motivo_postergacion: string;
  prioridad: string;
  ot_origen: {
    codigo_ot: string;
    fecha_cierre: string;
  };
  created_at: string;
}

interface ActividadesPostergadasSelectorProps {
  open: boolean;
  onClose: () => void;
  vehiculoId: string;
  otId: number;
  onAplicadas: () => void;
}

export function ActividadesPostergadasSelector({
  open,
  onClose,
  vehiculoId,
  otId,
  onAplicadas,
}: ActividadesPostergadasSelectorProps) {
  const [actividades, setActividades] = useState<ActividadPostergadaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [seleccionadas, setSeleccionadas] = useState<Set<number>>(new Set());
  const [aplicando, setAplicando] = useState(false);

  useEffect(() => {
    if (open && vehiculoId) {
      fetchActividadesPostergadas();
    }
  }, [open, vehiculoId]);

  const fetchActividadesPostergadas = async () => {
    try {
      setLoading(true);
      const response =
        await maintenanceService.getActividadesPostergadas(vehiculoId);
      setActividades(response.data || []);

      // Por defecto seleccionar las de prioridad ALTA y CRITICA
      const autoSeleccionadas = (response.data || [])
        .filter(
          (a: ActividadPostergadaItem) =>
            a.prioridad === "ALTA" || a.prioridad === "CRITICA",
        )
        .map((a: ActividadPostergadaItem) => a.id);

      setSeleccionadas(new Set(autoSeleccionadas));
    } catch (error) {
      console.error("Error fetching actividades postergadas:", error);
      toast.error("Error al cargar actividades postergadas");
    } finally {
      setLoading(false);
    }
  };

  const toggleSeleccion = (id: number) => {
    const nuevas = new Set(seleccionadas);
    if (nuevas.has(id)) {
      nuevas.delete(id);
    } else {
      nuevas.add(id);
    }
    setSeleccionadas(nuevas);
  };

  const seleccionarTodas = () => {
    if (seleccionadas.size === actividades.length) {
      setSeleccionadas(new Set());
    } else {
      setSeleccionadas(new Set(actividades.map((a) => a.id)));
    }
  };

  const handleAplicar = async () => {
    if (seleccionadas.size === 0) {
      toast.error("Selecciona al menos una actividad");
      return;
    }

    try {
      setAplicando(true);
      await maintenanceService.aplicarActividadesPostergadas(otId, {
        actividades_ids: Array.from(seleccionadas),
      });

      toast.success(
        `${seleccionadas.size} actividades agregadas a la orden de trabajo`,
      );
      onAplicadas();
      onClose();
    } catch (error) {
      console.error("Error aplicando actividades:", error);
      toast.error("Error al aplicar actividades postergadas");
    } finally {
      setAplicando(false);
    }
  };

  const getPrioridadColor = (prioridad: string) => {
    switch (prioridad) {
      case "CRITICA":
        return "bg-red-100 text-red-700 border-red-200";
      case "ALTA":
        return "bg-orange-100 text-orange-700 border-orange-200";
      case "MEDIA":
        return "bg-blue-100 text-blue-700 border-blue-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  // Función helper para obtener el precio de forma segura
  const obtenerPrecio = (actividad: ActividadPostergadaItem): number => {
    const precio = actividad.precio_cotizado ?? actividad.costo_estimado;
    return Number(precio) || 0;
  };

  const costoTotal = actividades
    .filter((a) => seleccionadas.has(a.id))
    .reduce((sum, a) => sum + obtenerPrecio(a), 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600" />
            Actividades Postergadas del Vehículo
          </DialogTitle>
          <DialogDescription>
            Este vehículo tiene actividades pendientes de mantenimientos
            anteriores. Selecciona las que deseas incluir en esta orden de
            trabajo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Resumen y controles */}
          <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Total Disponibles
                </p>
                <p className="text-2xl font-bold text-blue-600">
                  {actividades.length}
                </p>
              </div>
              <div className="h-12 w-px bg-blue-200" />
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Seleccionadas
                </p>
                <p className="text-2xl font-bold text-indigo-600">
                  {seleccionadas.size}
                </p>
              </div>
              <div className="h-12 w-px bg-blue-200" />
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Costo Estimado
                </p>
                <p className="text-2xl font-bold text-green-600">
                  S/ {costoTotal.toFixed(2)}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={seleccionarTodas}
              className="border-blue-300"
            >
              {seleccionadas.size === actividades.length
                ? "Deseleccionar Todas"
                : "Seleccionar Todas"}
            </Button>
          </div>

          {/* Lista de actividades */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-3" />
              <p className="text-sm text-gray-600">Cargando actividades...</p>
            </div>
          ) : actividades.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
              <CheckCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">
                No hay actividades postergadas
              </p>
              <p className="text-xs text-gray-400">
                Todas las actividades están al día
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {actividades.map((actividad) => {
                const estaSeleccionada = seleccionadas.has(actividad.id);
                const diasDesde = Math.floor(
                  (new Date().getTime() -
                    new Date(actividad.created_at).getTime()) /
                  (1000 * 60 * 60 * 24),
                );

                return (
                  <div
                    key={actividad.id}
                    className={`border-2 rounded-xl p-4 transition-all cursor-pointer ${estaSeleccionada
                        ? "border-blue-400 bg-blue-50"
                        : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                    onClick={() => toggleSeleccion(actividad.id)}
                  >
                    <div className="flex items-start gap-4">
                      {/* Checkbox */}
                      <div className="pt-1">
                        <Checkbox
                          checked={estaSeleccionada}
                          onCheckedChange={() => toggleSeleccion(actividad.id)}
                          className="h-5 w-5"
                        />
                      </div>

                      {/* Contenido principal */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h4 className="font-bold text-gray-900">
                              {actividad.item_catalogo.nombre}
                            </h4>
                            <p className="text-xs text-gray-500 mt-1">
                              Código: {actividad.item_catalogo.codigo_interno} •
                              Cant: {actividad.cantidad}{" "}
                              {actividad.item_catalogo.unidad_medida}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <Badge
                              className={getPrioridadColor(actividad.prioridad)}
                            >
                              {actividad.prioridad}
                            </Badge>
                            <span className="text-xs text-gray-400">
                              Hace {diasDesde} días
                            </span>
                          </div>
                        </div>

                        {/* Info adicional */}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                          <div className="flex items-center gap-2 text-xs">
                            <Calendar className="h-3 w-3 text-gray-400" />
                            <span className="text-gray-600">
                              OT: {actividad.ot_origen.codigo_ot}
                            </span>
                          </div>
                          {actividad.proveedor && (
                            <div className="flex items-center gap-2 text-xs">
                              <Package className="h-3 w-3 text-gray-400" />
                              <span className="text-gray-600 truncate">
                                {actividad.proveedor.razon_social}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-xs">
                            <DollarSign className="h-3 w-3 text-gray-400" />
                            <span className="font-medium text-green-600">
                              S/ {obtenerPrecio(actividad).toFixed(2)}
                            </span>
                          </div>
                        </div>

                        {/* Motivo de postergación */}
                        <div className="bg-amber-50 border border-amber-100 rounded-lg p-2">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="h-3 w-3 text-amber-600 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-amber-800">
                              <strong>Motivo:</strong>{" "}
                              {actividad.motivo_postergacion}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Información de ayuda */}
          {actividades.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h5 className="font-bold text-blue-900 text-sm mb-1">
                    ¿Qué sucede al aplicar actividades postergadas?
                  </h5>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>
                      ✓ Se agregarán como nuevas actividades a esta orden de
                      trabajo
                    </li>
                    <li>
                      ✓ Se mantendrá el proveedor y precio si ya estaban
                      seleccionados
                    </li>
                    <li>
                      ✓ Las actividades postergadas se marcarán como "aplicadas"
                    </li>
                    <li>
                      ✓ Podrás editarlas o eliminarlas si es necesario antes de
                      iniciar la OT
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <div className="flex items-center gap-3">
            {seleccionadas.size > 0 && (
              <div className="text-right">
                <p className="text-xs text-gray-500">
                  {seleccionadas.size} actividades seleccionadas
                </p>
                <p className="text-sm font-bold text-green-600">
                  Total: S/ {costoTotal.toFixed(2)}
                </p>
              </div>
            )}
            <Button
              onClick={handleAplicar}
              disabled={seleccionadas.size === 0 || aplicando}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {aplicando ? (
                <>
                  <Clock className="h-4 w-4 mr-2 animate-spin" />
                  Aplicando...
                </>
              ) : (
                <>Agregar a la OT ({seleccionadas.size})</>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
